import dayjs from 'dayjs'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams, useSearchParams } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { AllDealersHeader, DealerHeader } from '@/components/layout/DealerLayout'
import { StateContainer } from '@/components/ui/StateContainer'
import { useDealer } from '@/hooks/Dealer'
import RoIntakeNotEnabled from '@/pages/RoIntake/components/RoIntakeNotEnabled'
import SetStatusModal from '@/pages/RoIntake/SetStatusModal'
import { SLA_LATE } from '@/pages/RoStatusDashboard/buckets'
import { STAFF } from '@/pages/RoStatusDashboard/workload'
import { getWorkQueue, getWorkQueueCounts } from '@/services/repairOrders.service'
import { DashboardStoreContext } from '@/stores/DashboardStore'
import { UserStoreContext } from '@/stores/UserStore'

import OutstandingRow from './OutstandingRow'
import PendingRow from './PendingRow'
import {
  AGING_OUT_SOON_DAYS,
  cardFiltersFor,
  cardsFor,
  DISPOSITION,
  DUE_SOON_HOURS,
  GUIDELINE_HOURS,
  managedTypeFilter,
  QUEUE_GROUPS,
  QUEUES,
  queueById,
  rowPermissions,
  WORK_QUEUE_CHANGED,
} from './queue'
import QueueRow from './QueueRow'
import { formatDollars, GUIDELINE, idleNoteFor, KpiGroup, KpiRow, QueueTile } from './QueueTile'
import useRowEdits from './useRowEdits'

// Time in queue and the late flags move while the page is open.
const useNow = (intervalMs) => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(timer)
  }, [intervalMs])
  return now
}

/**
 * WorkQueue
 *
 * WARP-959 — every open RO, one queue per status (2026-10-01): Ready, Closed, admin
 * review, and Pending OEM to work, and dealer review waiting on the dealer. The header
 * total is the admin's to-do list. Emily: "if you have a number here, you have work to
 * do." Every queue lists the oldest received first, because the 24-hour rule is the one
 * in WPC's contract (Michelle).
 *
 * The queues are tiles across the top, the all-dealers dashboard's (WARP-965), with the
 * picked queue's filter cards under them. Picking the queue on screen again clears its
 * filter.
 *
 * Every tab is worked in place, as the Ready Queue was (WARP-951, WARP-966): each row
 * takes a status where its tab has moves, the dealer-closed flag, and attachments, then
 * an explicit Save; Save all commits every changed row on the tab. A row that got a
 * status leaves the queue. Outstanding List is the exception: its form has required
 * fields, so choosing it opens that form, which saves the RO itself. The Outstanding List
 * tabs have no status moves: those ROs are worked on the RO page, so their rows open it.
 */
const WorkQueue = () => {
  const { dealerId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const queue = queueById(searchParams.get('tab'))
  // The dealer staff this queue is narrowed to, from a count on the all-dealers dashboard
  // (WARP-960): `?admin=…&assistant_manager=…&manager=…`, each possibly repeated.
  const staffFilters = useMemo(
    () =>
      Object.values(STAFF)
        .map(({ label, param }) => ({ label, param, names: searchParams.getAll(param) }))
        .filter(({ names }) => names.length > 0),
    [searchParams],
  )
  const { isWpcUser, isWpcExecutive, isDealerUser, isDealerAdmin, isReadOnlySession } =
    useContext(UserStoreContext)
  const { type } = useContext(DashboardStoreContext)
  const isROServiceUser = isDealerUser && !isDealerAdmin
  const { dealer } = useDealer(dealerId)
  const now = useNow(60 * 1000)
  const today = dayjs(now).format('YYYY-MM-DD')

  const [rows, setRows] = useState([])
  const [counts, setCounts] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [savingAll, setSavingAll] = useState(false)
  const [olRow, setOlRow] = useState(null)
  const { dealer: olDealer } = useDealer(olRow?.dealer.id)
  // Manage Repair Order for one row, opened from its RO number.
  const [openRow, setOpenRow] = useState(null)
  const { dealer: openDealer } = useDealer(openRow?.dealer.id)

  // The Outstanding List queues open the RO page; the rest open Manage Repair Order.
  const isOutstanding = queue.kind === 'outstanding'
  const isPending = queue.kind === 'pending'

  const load = useCallback(
    async (signal, { quiet = false } = {}) => {
      if (!quiet) setLoading(true)
      const filters = {
        ...managedTypeFilter({ dealerId, isWpcExecutive, type }),
        ...Object.fromEntries(staffFilters.map(({ param, names }) => [param, names])),
      }
      const [response, nextCounts] = await Promise.all([
        getWorkQueue(dealerId || null, queue.api, setError, signal, filters),
        getWorkQueueCounts(dealerId || null, signal, filters),
      ])
      if (signal?.aborted) return
      setLoading(false)
      if (nextCounts) setCounts(nextCounts)
      if (response) {
        setRows(response)
        setError(null)
      }
    },
    [dealerId, queue.api, staffFilters, isWpcExecutive, type],
  )

  useEffect(() => {
    const controller = new AbortController()
    setRows([])
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  const announceChange = () => window.dispatchEvent(new Event(WORK_QUEUE_CHANGED))

  const dropRows = (ids) => setRows((prev) => prev.filter((row) => !ids.includes(row.id)))

  // Staged edits, by RO. Kept across tabs, so switching tabs never loses unsaved work, but
  // not across dealers: nothing staged for the last dealer can be saved from the new
  // dealer's queue (PR review, WARP-959).
  const { commit, dropEdits, editFor, rowsToSave, saving, setEdits, updateEdit } = useRowEdits({
    resetKey: dealerId,
    onSaved: ({ saved }) => {
      // Worked rows leave the queue straight away; the refetch then picks up new file
      // lists for rows that stay, and the new counts.
      dropRows(saved.filter(({ edit }) => edit.disposition).map(({ row }) => row.id))
      announceChange()
      load(undefined, { quiet: true })
    },
  })

  // Close anything open for the last dealer, too.
  useEffect(() => {
    setOlRow(null)
    setOpenRow(null)
  }, [dealerId])

  const changeRow = (row, patch) => {
    // Outstanding List needs its form. Open it with the row's other choices carried in.
    if (patch.disposition === DISPOSITION.OUTSTANDING) setOlRow(row)
    updateEdit(row, patch)
  }

  // The summary cards' filters, which also filter the table. See `cardFiltersFor`.
  const cardFilters = useMemo(() => cardFiltersFor(queue, now, today), [queue, now, today])

  // Which summary card is filtering the table, from `?filter=` so a link can open the
  // queue already filtered: the dashboard's Urgency cards do (WARP-960). Anything this
  // queue has no card for is the unfiltered view, 'all'.
  const requestedFilter = searchParams.get('filter')
  const filter = cardFilters[requestedFilter] ? requestedFilter : 'all'
  const setFilter = (id) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (id === 'all') next.delete('filter')
        else next.set('filter', id)
        return next
      },
      { replace: true },
    )

  // Pick a queue. Staff filters stay; the card filter is the last queue's, so it goes.
  // Picking the queue already on screen clears its filter, back to the whole queue.
  const selectQueue = (id) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('filter')
      if (id === QUEUES[0].id) next.delete('tab')
      else next.set('tab', id)
      return next
    })

  const visibleRows = useMemo(
    () => (cardFilters[filter] ? rows.filter(cardFilters[filter]) : rows),
    [rows, filter, cardFilters],
  )

  // Outstanding List is saved through its own form, so Save all leaves those rows out.
  const pendingRows = rowsToSave(rows)

  // Who changes status here: WPC, and a self-managed dealer's own admins, who run their
  // own list (Emily, 2026-10-06). Every user can add files. See `rowPermissions`.
  const canWork = isWpcUser || (isDealerAdmin && Boolean(dealer?.isDealerManaged))
  const permissions = rowPermissions({ canWork, isReadOnlySession })
  const canEditRows = permissions.canChangeStatus || permissions.canAttach
  const cards = cardsFor(queue, rows, cardFilters)

  const emptyMessages = {
    late: 'Nothing is past the SLA.',
    soon: `Nothing comes due in the next ${DUE_SOON_HOURS} hours.`,
    waiting: `Nothing has waited over ${GUIDELINE_HOURS} hours.`,
    agedOut: 'Nothing has aged out.',
    agingOutSoon: `Nothing ages out in the next ${AGING_OUT_SOON_DAYS} days.`,
    over30: 'Nothing has been out over 30 days.',
    over90: 'Nothing has been out over 90 days.',
  }

  // The row's own columns, then the ones every row ends with (see RowActionCells).
  let columns = ['RO', 'Received', 'Time in Queue', 'SLA due']
  if (isOutstanding) {
    columns = ['RO', 'Received', `Waiting on ${queue.waitingOn}`, 'Ages out', 'Actions needed']
  }
  if (isPending) {
    columns = ['RO', 'Received', 'Age', 'Claimed', 'Last updated']
  }
  columns = [
    ...columns,
    ...(permissions.canChangeStatus && queue.dispositions.length ? ['Change Status'] : []),
    ...(permissions.canChangeStatus ? ['Dealer Closed'] : []),
    'Files',
    null,
  ]

  const rowProps = (row) => ({
    edit: editFor(row),
    now,
    onChange: (patch) => changeRow(row, patch),
    onSave: () => commit([row]),
    permissions,
    row,
    saving: Boolean(saving[row.id]),
    showDealer: !dealerId,
  })

  return (
    <>
      <Helmet>
        <title>{dealer?.name ? `Work Queue · ${dealer.name}` : 'Work Queue'}</title>
      </Helmet>

      {dealerId ? (
        <DealerHeader dealer={dealer} isROServiceUser={isROServiceUser} />
      ) : (
        // Same band as the dealer header, so the page reads the same with or without a
        // dealer picked.
        <AllDealersHeader />
      )}

      {dealer?.isRoIntakeEnabled === false ? (
        <RoIntakeNotEnabled
          dealerId={dealerId}
          what="The work queue holds repair orders logged through RO Intake."
        />
      ) : (
        <div className="flex w-full flex-col space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-charcoal-900 text-lg font-semibold">
                Work Queue
                {counts && (
                  <span className="text-charcoal-500 ml-2 text-sm font-normal">
                    {counts.total === 0
                      ? 'Nothing to work'
                      : `${counts.total.toLocaleString()} to work`}
                  </span>
                )}
                {!permissions.canChangeStatus && (
                  <span
                    className="bg-charcoal-200 text-charcoal-700 ml-2 rounded-full px-2 py-0.5 align-middle text-[10px] font-bold uppercase tracking-widest"
                    title={
                      permissions.canAttach
                        ? 'You can add files to these repair orders, but not change their status.'
                        : 'You can see these repair orders here, but not change them.'
                    }
                  >
                    {permissions.canAttach ? 'Files only' : 'View only'}
                  </span>
                )}
              </h2>
              <p className="text-charcoal-500 mt-1 text-sm">{queue.description}</p>
            </div>

            {canEditRows && (
              <button
                className="flex h-[38px] items-center gap-2 rounded border-[1.5px] border-blue-800 bg-blue-800 px-4 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-blue-900 hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={savingAll || pendingRows.length === 0}
                onClick={async () => {
                  setSavingAll(true)
                  await commit(pendingRows)
                  setSavingAll(false)
                }}
                type="button"
              >
                {savingAll
                  ? 'Saving…'
                  : `Save all${pendingRows.length ? ` (${pendingRows.length})` : ''}`}
              </button>
            )}
          </div>

          {staffFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" aria-label="Narrowed to">
              <span className="text-charcoal-500 text-xs font-semibold">Narrowed to</span>
              {staffFilters.map(({ label, param, names }) => (
                <button
                  className="bg-charcoal-100 text-charcoal-800 hover:bg-charcoal-200 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors"
                  key={param}
                  onClick={() =>
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev)
                      next.delete(param)
                      return next
                    })
                  }
                  title={`Show every ${label.toLowerCase()}`}
                  type="button"
                >
                  <span className="text-charcoal-500 font-normal">{label}:</span>
                  {names.join(', ')}
                  <span aria-hidden className="text-charcoal-400">
                    ✕
                  </span>
                  <span className="sr-only">, remove</span>
                </button>
              ))}
            </div>
          )}

          {/* The queues as tiles, in their two groups (WARP-965, Mark: "I'd rather see
              those big tiles on the work screen"). A tile picks its queue; the one on
              screen, clicked again, clears the filter below it. */}
          <KpiRow role="group" aria-label="Work queues">
            {QUEUE_GROUPS.map((group) => {
              const groupQueues = QUEUES.filter((option) => option.group === group.id)
              return (
                <KpiGroup key={group.id} label={group.label} tiles={groupQueues.length}>
                  {groupQueues.map((option) => (
                    <QueueTile
                      counts={counts?.queues?.[option.countsKey]}
                      idleNote={idleNoteFor(option)}
                      key={option.id}
                      loading={!counts}
                      onClick={() => selectQueue(option.id)}
                      queue={option}
                      selected={option.id === queue.id}
                    />
                  ))}
                </KpiGroup>
              )
            })}
          </KpiRow>

          {/* The picked queue's filters, smaller, under the tiles. No "in total" card: the
              tile above is the whole queue. */}
          <div
            className="relative flex flex-wrap gap-3 lg:-m-1 lg:flex-nowrap lg:overflow-x-auto lg:p-1"
            role="group"
            aria-label={`Filter ${queue.label}`}
          >
            {cards.map((card) => {
              const pressed = filter === card.id
              let accent = null
              if (card.alert) accent = SLA_LATE
              else if (card.warn) accent = GUIDELINE

              return (
                <button
                  aria-pressed={pressed}
                  className={mergeClassNames(
                    'hover:bg-charcoal-50 flex min-w-[9rem] flex-1 basis-0 flex-col gap-0.5 rounded-xl bg-white px-4 py-2.5 text-left shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-800 lg:max-w-[18rem]',
                    accent && 'border-l-4',
                    pressed && 'ring-2 ring-blue-800',
                  )}
                  key={card.id}
                  onClick={() => setFilter(pressed ? 'all' : card.id)}
                  style={accent ? { borderColor: accent.swatch } : undefined}
                  type="button"
                >
                  <span className="text-charcoal-500 flex-1 text-xs font-semibold">
                    {card.label}
                  </span>
                  <span className="flex items-baseline gap-2">
                    <span
                      className="text-lg font-semibold tabular-nums"
                      style={accent ? { color: accent.ink } : undefined}
                    >
                      {loading ? '—' : card.value}
                    </span>
                    {/* The claim dollars, smaller: "You don't have to make it in big blinky
                        letters, but at least you could have the dollars" (Mark). */}
                    {!loading && card.dollars !== null && card.value > 0 && (
                      <span className="text-charcoal-500 text-xs font-semibold tabular-nums">
                        {formatDollars(card.dollars)}
                      </span>
                    )}
                  </span>
                </button>
              )
            })}
          </div>

          <StateContainer error={error}>
            {/* `relative`, so the chips' and switches' screen-reader inputs stay in the scroll box
                and do not widen the page. */}
            <div
              className="relative overflow-x-auto rounded-2xl bg-white shadow-sm"
              id="work-queue-panel"
            >
              <table className="w-full min-w-[1100px] border-collapse">
                <thead>
                  <tr className="text-charcoal-700 text-left text-[10px] font-bold uppercase tracking-widest">
                    {columns.map((label, index) => (
                      <th
                        className={
                          index === 0 || index === columns.length - 1
                            ? 'whitespace-nowrap px-4 py-3'
                            : 'whitespace-nowrap p-3'
                        }
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                      >
                        {label || <span className="sr-only">Actions</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const props = rowProps(row)
                    if (isOutstanding) {
                      return <OutstandingRow key={row.id} {...props} today={today} />
                    }
                    if (isPending) {
                      return (
                        <PendingRow
                          key={row.id}
                          {...props}
                          dispositions={queue.dispositions}
                          onOpen={() => setOpenRow(row)}
                        />
                      )
                    }
                    return (
                      <QueueRow
                        key={row.id}
                        {...props}
                        dispositions={queue.dispositions}
                        onOpen={() => setOpenRow(row)}
                      />
                    )
                  })}

                  {!loading && visibleRows.length === 0 && (
                    <tr>
                      <td
                        className="text-charcoal-400 px-4 py-10 text-center text-sm"
                        colSpan={columns.length}
                      >
                        {rows.length === 0 ? queue.empty : emptyMessages[filter]}
                      </td>
                    </tr>
                  )}
                  {loading && rows.length === 0 && (
                    <tr>
                      <td
                        className="text-charcoal-400 px-4 py-10 text-center text-sm"
                        colSpan={columns.length}
                      >
                        Loading…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </StateContainer>
        </div>
      )}

      {olRow && (
        <SetStatusModal
          dealer={olDealer}
          dealerId={olRow.dealer.id}
          initialDealerClosed={editFor(olRow).dealerClosed}
          initialNewFiles={editFor(olRow).newFiles}
          initialStatus={DISPOSITION.OUTSTANDING}
          isDealerAdmin={isDealerAdmin}
          isDealerUser={isDealerUser}
          onSuccess={() => {
            dropRows([olRow.id])
            dropEdits([olRow.id])
            announceChange()
            load(undefined, { quiet: true })
          }}
          open={Boolean(olRow)}
          repairOrder={olRow}
          setOpen={(open) => {
            if (open) return
            // Closed without saving: the Outstanding List fields are required, so the
            // choice cannot stand without them. Files and the flag stay staged.
            setEdits((prev) =>
              prev[olRow.id]
                ? { ...prev, [olRow.id]: { ...prev[olRow.id], disposition: null } }
                : prev,
            )
            setOlRow(null)
          }}
        />
      )}

      {openRow && (
        <SetStatusModal
          dealer={openDealer}
          dealerId={openRow.dealer.id}
          isDealerAdmin={isDealerAdmin}
          isDealerUser={isDealerUser}
          onSuccess={() => {
            // The modal saved the RO itself, so anything staged on its row is stale. The
            // reload drops the row if it left the queue.
            dropEdits([openRow.id])
            announceChange()
            load(undefined, { quiet: true })
          }}
          open={Boolean(openRow)}
          repairOrder={openRow}
          setOpen={(open) => {
            if (!open) setOpenRow(null)
          }}
        />
      )}
    </>
  )
}

export default WorkQueue
